import { useQueryClient } from '@tanstack/react-query'
import { Home } from 'lucide-react'
import { useState } from 'react'

import { pedir } from '../api/nucleo'
import { Aviso, Boton, Modal, Pastilla } from '../componentes/ui'

/**
 * La portada de una web (la página que se abre en «/»). Cuál es lo dice el núcleo
 * (`esPortada`, `apps/nucleo/src/contenido/portada.ts`): aquí no se repite la regla.
 * La portada no se borra ni deja de serlo al guardar; para cambiarla, «Usar como portada»
 * en la página que se quiere, que cambia las dos de una vez.
 */

/** La pastilla, para el listado y el editor. */
export const MarcaDePortada = () => (
  <span title="Es la página que se abre en la dirección principal de tu web.">
    <Pastilla clave="violeta" punto={false}>
      Portada
    </Pastilla>
  </span>
)

/** El título en el listado de páginas, con la marca si es la portada. */
export const TituloConPortada = ({ titulo, esPortada }: { esPortada: boolean; titulo: string }) => (
  <span className="inline-flex flex-wrap items-center gap-2">
    {titulo || <span className="text-tenue">Sin título</span>}
    {esPortada && <MarcaDePortada />}
  </span>
)

type Resultado = { portada: { id: number; titulo: string }; anterior: null | { id: number; titulo: string; slug: string } }

/** «Usar como portada», en la cabecera del editor de una página que no lo es. */
export const UsarComoPortada = ({ sitio, id, titulo, sucio, alTerminar }: { alTerminar: () => void; id: number; sitio: number; sucio: boolean; titulo: string }) => {
  const clienteDeConsultas = useQueryClient()
  const [abierto, setAbierto] = useState(false)
  const [ocupado, setOcupado] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)
  const [hecho, setHecho] = useState<null | Resultado>(null)

  const cambiar = async () => {
    setOcupado(true)
    setFallo(null)
    try {
      const r = await pedir<Resultado>('/panel/paginas/portada', { metodo: 'POST', cuerpo: { sitio, id } })
      setHecho(r)
      void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', 'paginas'] })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['diseno-estado'] })
    } catch (e) {
      setFallo(e instanceof Error ? e.message : 'No se ha podido cambiar la portada.')
    } finally {
      setOcupado(false)
    }
  }

  const cerrar = () => {
    setAbierto(false)
    setFallo(null)
    if (hecho) {
      setHecho(null)
      alTerminar()
    }
  }

  return (
    <>
      <Boton disabled={sucio} icono={<Home />} onClick={() => setAbierto(true)} pequeno title={sucio ? 'Guarda antes tus cambios.' : undefined} tipo="discreto">
        Usar como portada
      </Boton>
      {abierto && (
        <Modal
          ancho="max-w-md"
          cerrar={() => !ocupado && cerrar()}
          pie={
            hecho ? (
              <Boton onClick={cerrar} tipo="primario">
                Entendido
              </Boton>
            ) : (
              <>
                <Boton disabled={ocupado} onClick={cerrar} tipo="discreto">
                  Cancelar
                </Boton>
                <Boton cargando={ocupado} onClick={() => void cambiar()} tipo="primario">
                  Usar como portada
                </Boton>
              </>
            )
          }
          titulo={hecho ? 'Portada cambiada' : `¿Usar «${titulo}» como portada?`}
        >
          {hecho ? (
            <div className="flex flex-col gap-2 text-sm text-tinta-2">
              <p>«{hecho.portada.titulo}» es ahora la página que se abre en la dirección principal de tu web.</p>
              {hecho.anterior && (
                <p>
                  La portada de antes, «{hecho.anterior.titulo}», sigue publicada en <code className="rounded bg-papel-2 px-1">/{hecho.anterior.slug}</code>.
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3 text-sm text-tinta-2">
              <p>Pasará a abrirse en la dirección principal de tu web, en lugar de la portada de ahora, que conservará su contenido con una dirección propia.</p>
              <p>El cambio se ve en tu web al momento: las dos páginas siguen publicadas como están.</p>
              {fallo && <Aviso tono="error">{fallo}</Aviso>}
            </div>
          )}
        </Modal>
      )}
    </>
  )
}
